/**
 * Telugu (India) locale for extensions-ui.
 * Export is the "extensions" subtree: host apps merge this into their i18n
 * resources as translation.extensions so that t('extensions.list.heading') etc. resolve.
 */
import type { ExtensionsLocale } from './en'

export const extensionsTeIn = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `రద్దు చేయి`,
    delete: `తొలగించు`,
    save: `సేవ్ చేయి`,
    apply: `వర్తింపజేయి`,
    edit: `సవరించు`,
    actions: `చర్యలు`,
    add: `జోడించు`,
    all: `అన్నీ`,
    loading: `లోడ్ అవుతోంది...`,
    noChange: `మార్పు లేదు`,
    none: `ఏమీ లేదు`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    pageLabel: `అన్ని ఎక్స్‌టెన్షన్లు`,
    heading: `ఎక్స్‌టెన్షన్లు`,
    linesHeading: `లైన్లు`,
    searchPlaceholder: `పేరు లేదా నంబర్ ద్వారా శోధించండి`,
    linesSearchPlaceholder: `పేరు, నంబర్, కేటాయించిన ప్రొఫైల్ లేదా స్థానం ద్వారా శోధించండి`,
    loadingExtensions: `ఎక్స్‌టెన్షన్లు లోడ్ అవుతున్నాయి...`,
    noMatch: `మీ శోధనకు సరిపోయే ఎక్స్‌టెన్షన్లు లేవు`,
    noExtensions: `ఎక్స్‌టెన్షన్లు కనుగొనబడలేదు`,
    noMatchHint: `వేరే శోధన పదాన్ని ప్రయత్నించండి.`,
    noExtensionsHint: `ఈ PBX కోసం ఎక్స్‌టెన్షన్లు లేవు.`,
    activateAll: `అన్నీ సక్రియం చేయి`,
    deactivateAll: `అన్నీ నిష్క్రియం చేయి`,
    massEdit: `సామూహిక సవరణ`,
    linesSelected: `{{count}} లైన్ ఎంపిక చేయబడింది`,
  },

  // ---------------------------------------------------------------------------
  // Column headers
  // ---------------------------------------------------------------------------
  columns: {
    number: `నంబర్`,
    name: `పేరు`,
    type: `రకం`,
    created: `సృష్టించబడింది`,
    updated: `నవీకరించబడింది`,
    lastUpdated: `చివరి నవీకరణ`,
    assignedUser: `కేటాయించిన వినియోగదారు`,
    assignedProfile: `కేటాయించిన ప్రొఫైల్`,
    location: `స్థానం`,
    actions: `చర్యలు`,
  },

  // ---------------------------------------------------------------------------
  // Row actions & badges
  // ---------------------------------------------------------------------------
  row: {
    inactive: `నిష్క్రియం`,
    edit: `సవరించు`,
    delete: `తొలగించు`,
    viewCallMetrics: `కాల్ మెట్రిక్స్ చూడండి`,
    activate: `సక్రియం చేయి`,
    deactivate: `నిష్క్రియం చేయి`,
    extensionFallback: `ఎక్స్‌టెన్షన్ {{id}}`,
    selectAllLines: `అన్ని లైన్‌లను ఎంచుకోండి`,
    selectLine: `{{name}} ఎంచుకోండి`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `ఎక్స్‌టెన్షన్ తొలగించు`,
    confirmation: `మీరు "{{displayName}}" ను తొలగించాలనుకుంటున్నారా? ఈ చర్యను రద్దు చేయలేరు.`,
    unnamed: `పేరు లేని ఎక్స్‌టెన్షన్`,
    reassignLabel: `సూచనలను పునఃకేటాయించు`,
    deleteWithoutReassigning: `పునఃకేటాయింపు లేకుండా తొలగించు`,
  },

  // ---------------------------------------------------------------------------
  // Set active state dialog (single)
  // ---------------------------------------------------------------------------
  activeStateDialog: {
    activateTitle: `ఎక్స్‌టెన్షన్ సక్రియం చేయి`,
    deactivateTitle: `ఎక్స్‌టెన్షన్ నిష్క్రియం చేయి`,
    body: `మీరు {{displayName}} ను {{action}} చేయబోతున్నారు, ఇది ఈ ఎక్స్‌టెన్షన్‌ను ప్రాథమిక లైన్ ఎక్స్‌టెన్షన్‌గా ఉపయోగిస్తున్న అన్ని పరికరాలను రీబూట్ చేస్తుంది.`,
    unnamedLine: `పేరు లేని లైన్`,
    confirmPrompt: `మీరు కొనసాగించాలనుకుంటున్నారా?`,
    setActive: `సక్రియంగా సెట్ చేయి`,
    setInactive: `నిష్క్రియంగా సెట్ చేయి`,
  },

  // ---------------------------------------------------------------------------
  // Bulk active state dialog
  // ---------------------------------------------------------------------------
  bulkActiveDialog: {
    activateTitle: `అన్ని ఎక్స్‌టెన్షన్లను సక్రియం చేయి`,
    deactivateTitle: `అన్ని ఎక్స్‌టెన్షన్లను నిష్క్రియం చేయి`,
    body: `మీరు అన్ని ఎక్స్‌టెన్షన్లను {{action}} చేయబోతున్నారు, ఇది ఈ ఎక్స్‌టెన్షన్లను ప్రాథమిక లైన్ ఎక్స్‌టెన్షన్‌గా ఉపయోగిస్తున్న అన్ని పరికరాలను రీబూట్ చేస్తుంది.`,
    confirmPrompt: `మీరు కొనసాగించాలనుకుంటున్నారా?`,
    activateAll: `అన్నీ సక్రియం చేయి`,
    deactivateAll: `అన్నీ నిష్క్రియం చేయి`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `లైన్ల సామూహిక సవరణ`,
    description: `{{count}} లైన్ కోసం కింది ఫీల్డ్‌లను నవీకరించండి. ప్రస్తుత విలువను ఉంచడానికి "మార్పు లేదు" గా వదిలివేయండి.`,
    outboundCallerId: `అవుట్‌బౌండ్ కాలర్ ID`,
    localPresence: `స్థానిక ప్రెజెన్స్`,
    preferredCodec: `ప్రాధాన్య కోడెక్`,
    location: `స్థానం`,
    locationPlaceholder: `మార్పు లేదు లేదా శోధించడానికి టైప్ చేయండి`,
    locationSearchLabel: `స్థానం శోధన`,
    loadingLocations: `స్థానాలు లోడ్ అవుతున్నాయి\u2026`,
    noLocationsFound: `స్థానాలు కనుగొనబడలేదు`,
    typeToSearchLocations: `స్థానాలను శోధించడానికి టైప్ చేయండి`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `పేజీకి ఫలితాలు:`,
    previousPage: `మునుపటి పేజీ`,
    nextPage: `తరువాతి పేజీ`,
    all: `అన్నీ`,
    jumpToPage: `పేజీ {{page}} కు వెళ్ళండి`,
    searchExtensions: `ఎక్స్‌టెన్షన్లు శోధించండి`,
    toggleFilters: `ఫిల్టర్లు టోగుల్ చేయి`,
    showingAll: `మొత్తం {{total}} ఫలితాలు చూపిస్తోంది`,
    showingRange: `{{start}} నుండి {{end}} వరకు, మొత్తం {{total}} ఫలితాలు`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deleteSuccess: `ఎక్స్‌టెన్షన్ విజయవంతంగా తొలగించబడింది.`,
    deleteFailed: `తొలగింపు విఫలమైంది`,
    deleteFailedDescription: `ఎక్స్‌టెన్షన్ తొలగించడం విఫలమైంది. దయచేసి మళ్ళీ ప్రయత్నించండి.`,
    success: `విజయం`,
    failed: `విఫలం`,
    activeStateSuccess: `లైన్ {{state}} గా సెట్ చేయబడింది.`,
    activeStateFailed: `లైన్ యాక్టివ్ స్థితిని నవీకరించడం విఫలమైంది.`,
    bulkSuccess: `{{count}} లైన్ {{state}} గా సెట్ చేయబడింది.`,
    partialFailure: `పాక్షిక వైఫల్యం`,
    partialFailureDescription: `{{succeeded}} విజయవంతం, {{failed}} విఫలం. కొన్ని లైన్లు నవీకరించబడకపోవచ్చు.`,
    massEditSuccess: `{{count}} లైన్ నవీకరించబడింది.`,
    massEditFailed: `సామూహిక సవరణ విఫలమైంది`,
    massEditFailedDescription: `ఎంపిక చేసిన లైన్లను నవీకరించడం విఫలమైంది. మళ్ళీ ప్రయత్నించండి.`,
  },

  // ---------------------------------------------------------------------------
  // Extension type labels
  // ---------------------------------------------------------------------------
  types: {
    line: `లైన్`,
    ringGroup: `రింగ్ గ్రూప్`,
    virtualFax: `వర్చువల్ ఫ్యాక్స్ మెషిన్`,
    conferenceRoom: `కాన్ఫరెన్స్ రూమ్`,
    switchboard: `స్విచ్‌బోర్డ్`,
    sharedVoicemail: `షేర్డ్ వాయిస్‌మెయిల్ బాక్స్`,
    callQueue: `కాల్ క్యూ`,
  },

  // ---------------------------------------------------------------------------
  // View mode labels
  // ---------------------------------------------------------------------------
  viewModes: {
    extensions: `ఎక్స్‌టెన్షన్లు`,
    lines: `లైన్లు`,
    ringGroups: `రింగ్ గ్రూప్‌లు`,
    callQueues: `కాల్ క్యూలు`,
    conferenceRooms: `కాన్ఫరెన్స్ రూమ్‌లు`,
    virtualFax: `వర్చువల్ ఫ్యాక్స్`,
    sharedVoicemail: `షేర్డ్ వాయిస్‌మెయిల్`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    title: `ఫిల్టర్`,
    status: `స్థితి:`,
    assignedUser: `కేటాయించిన వినియోగదారు:`,
    assignedProfile: `కేటాయించిన ప్రొఫైల్:`,
    location: `స్థానం:`,
    active: `సక్రియం`,
    inactive: `నిష్క్రియం`,
    all: `అన్నీ`,
    clearFilters: `ఫిల్టర్లను క్లియర్ చేయి`,
    apply: `వర్తింపజేయి`,
  },

  placeholder: {
    notImplemented: `{{typeLabel}} సవరణ పేజీ ఇంకా అమలు చేయలేదు.`,
    backToList: `{{listLabel}}కి తిరిగి వెళ్లండి`,
  },

  edit: {
    validationError: `ధృవీకరణ లోపం`,
    reviewRequiredFields: `దయచేసి తప్పనిసరి ఫీల్డ్‌లను సమీక్షించి మళ్ళీ ప్రయత్నించండి.`,
    emailFormatInvalid: `ఇమెయిల్ ఫార్మాటింగ్ తప్పుగా ఉంది. ఖాళీలు లేవని నిర్ధారించుకోండి. బహుళ చిరునామాలను కామాలతో వేరు చేయండి.`,
    nameNumberRequired: `పేరు మరియు నంబర్ తప్పనిసరి.`,
    reservedNumber: `{{number}} రిజర్వ్ చేయబడింది. దయచేసి వేరే నంబర్‌ను ఎంచుకోండి.`,
    createFailed: `సృష్టించడం విఫలమైంది`,
    updateFailed: `నవీకరణ విఫలమైంది`,
    breadcrumbAdd: `{{typeLabel}} జోడించు`,
    breadcrumbEdit: `{{typeLabel}} {{value}} సవరించు`,
    backToList: `← {{listLabel}}కి తిరిగి వెళ్లండి`,
    wrongPbx: `ఈ ఎక్స్‌టెన్షన్ ఎంచుకున్న PBXలో లేదు`,
    wrongPbxDescription: `ఈ ఎక్స్‌టెన్షన్ ఎంచుకున్న PBXకి చెందినది కాదు.`,
    openInPbx: `{{pbxName}}లో తెరవండి`,
    loadFailed: `ఈ ఎక్స్‌టెన్షన్‌ను లోడ్ చేయడం సాధ్యం కాలేదు`,
    options: `ఎంపికలు`,
    saveChanges: `మార్పులను సేవ్ చేయండి`,
    omitFromDirectory: `డైరెక్టరీ నుండి మినహాయించు`,
    nameRequired: `పేరు తప్పనిసరి`,
    numberRequired: `నంబర్ తప్పనిసరి`,
    extensionNumberPlaceholder: `ఎక్స్‌టెన్షన్ నంబర్`,
    yes: `అవును`,
    no: `కాదు`,
    members: `సభ్యులు`,
    membersNoun: `సభ్యులు`,
    userGroups: `వినియోగదారు గ్రూప్‌లు`,
    userGroupsNoun: `వినియోగదారు గ్రూప్‌లు`,
    holdMusic: `హోల్డ్ మ్యూజిక్`,
    line: {
      requiredFields: `పేరు, నంబర్, లొకేషన్ మరియు కేటాయించిన ప్రొఫైల్ అవసరం.`,
      selectLocation: `లొకేషన్‌ని ఎంచుకోండి`,
      notType: `ఇది లైన్ కాదు`,
      pbxUuid: `PBX UUID`,
      pbxName: `PBX`,
      sipSecret: `SIP సీక్రెట్`,
      sipSecretReset: `SIP సీక్రెట్ రీసెట్ చేయబడింది`,
      reset: `రీసెట్ చేయి`,
      extensionUuid: `ఎక్స్‌టెన్షన్ UUID`,
      dialNow: `డయల్ నౌ`,
      dialNowCleared: `డయల్-నౌ క్లియర్ చేయబడింది`,
      clear: `క్లియర్ చేయి`,
      assignedProfileMobileDevices: `కేటాయించిన ప్రొఫైల్ యొక్క మొబైల్ పరికరాలు`,
      addTitle: `లైన్ జోడించు`,
      title: `లైన్`,
      voicemailOptions: `వాయిస్‌మెయిల్`,
      outDialExtension: `OutDial ఎక్స్‌టెన్షన్`,
      outDialHelp: `OutDial ఎక్స్‌టెన్షన్ సహాయం`,
      outDialHelpNo: `కాదు: ఈ ఎక్స్‌టెన్షన్ సాధారణ ఎక్స్‌టెన్షన్‌గా పనిచేస్తుంది.`,
      outDialHelpYes: `అవును: ఈ ఎక్స్‌టెన్షన్ ఫార్వార్డింగ్ నంబర్‌కు మాత్రమే ఫార్వార్డ్ చేస్తుంది.`,
      outDialHelpBeAware: `గమనించండి:`,
      outDialHelpWarningOne: `కాల్ బయటి నంబర్‌కు ఫార్వార్డ్ అయిన తర్వాత సిస్టమ్ దాన్ని తిరిగి పొందడం సాధ్యం కాదు.`,
      outDialHelpWarningTwo: `OutDial ఎక్స్‌టెన్షన్‌తో మీరు సిస్టమ్ వాయిస్‌మెయిల్‌ను ఉపయోగించలేరు.`,
      outDialEnabledHint: `OutDial ప్రారంభించబడింది. కాల్‌లు ఫార్వార్డింగ్ నంబర్‌కు ఫార్వార్డ్ అవుతాయి; అవుట్‌బౌండ్ కాలర్ ID ఇప్పటికీ అవుట్‌బౌండ్ లెగ్‌పై వర్తిస్తుంది.`,
      outDialDisabledHint: `OutDial నిలిపివేయబడింది. అవుట్‌బౌండ్ కాలర్ సెట్టింగ్‌లు వర్తిస్తాయి.`,
      loadingProfiles: `ప్రొఫైల్‌లు లోడ్ అవుతున్నాయి…`,
      voicemailEnabled: `వాయిస్‌మెయిల్ ప్రారంభించబడింది`,
      forwardingNumber: `ఫార్వార్డింగ్ నంబర్ (OutDial)`,
      localAreaCode: `స్థానిక ఏరియా కోడ్`,
      localAreaCodeInvalid: `ఏరియా కోడ్ చెల్లదు. స్థానిక ఏరియా కోడ్ కోసం {{areaCode}} అనుమతించబడదు.`,
      localAreaCodeNotAllowed: `స్థానిక ఏరియా కోడ్ కోసం ఏరియా కోడ్ {{areaCode}} అనుమతించబడదు.`,
      outboundCallerIdRequired: `దయచేసి అవుట్‌బౌండ్ కాలర్ ID ని ఎంచుకోండి.`,
      outboundCallerIdNoOptions: `ఈ PBX లో ఫోన్ నంబర్‌లు కాన్ఫిగర్ చేయబడలేదు. ఈ లైన్‌ను సవరించే ముందు PBX కు ఫోన్ నంబర్‌ను జోడించండి.`,
      outboundCallerIdMissingSavedValue: `ఈ లైన్‌లో చెల్లుబాటు అయ్యే అవుట్‌బౌండ్ కాలర్ ID లేదు. కొనసాగించే ముందు ఒకదాన్ని ఎంచుకుని సేవ్ చేయండి.`,
      expandDetails: `వివరాలు చూపించు`,
      collapseDetails: `వివరాలు దాచు`,
      activate: `సక్రియం చేయి`,
      deactivate: `నిష్క్రియం చేయి`,
      findMe: {
        title: `నన్ను కనుగొను`,
        mode: `నన్ను కనుగొను`,
        number: `నన్ను కనుగొను నంబర్`,
        delay: `నన్ను కనుగొను ప్రారంభం`,
        modeOptions: {
          '0': `ఆఫ్`,
          '1': `ఆన్`,
          '2': `అందరినీ ఒకేసారి మోగించు`,
        },
        delayOptions: {
          '0': `వెంటనే`,
          '5': `5 సెకన్లు`,
          '10': `10 సెకన్లు`,
          '15': `15 సెకన్లు`,
        },
        numberRequired: `నన్ను కనుగొను ఆన్‌లో ఉన్నప్పుడు నన్ను కనుగొను నంబర్ తప్పనిసరి.`,
        numberInvalid: `నన్ను కనుగొను నంబర్‌లో అంకెలు మరియు ఐచ్చికంగా ముందు + మాత్రమే ఉండాలి (గరిష్టం 15 అంకెలు).`,
      },
      callRecording: `కాల్ రికార్డింగ్`,
      callRecordingOptions: {
        '0': `కాదు`,
        '1': `అవును`,
        '2': `సిస్టమ్ నిర్వచించినది`,
      },
      dedicatedFax: `ప్రత్యేక ఫ్యాక్స్`,
      inCallFeatureCodes: `ఇన్-కాల్ ఫీచర్ కోడ్‌లు ప్రారంభించబడ్డాయి`,
      createButton: `లైన్ సృష్టించు`,
      saveAndCreateAnother: `సేవ్ చేసి మరొకటి సృష్టించండి`,
      details: `లైన్ వివరాలు`,
      codecOpus: `నెమ్మది ఇంటర్నెట్‌కు ఉత్తమం (OPUS) మద్దతు: వెబ్ ఫోన్‌లు, Yealink`,
      codecPcmu: `ప్రామాణికం (PCMU) మద్దతు: అన్ని ఫోన్ రకాలు`,
      codecG722: `HD ఆడియో (G722) మద్దతు: Yealink, Cisco`,
      codecG729: `కాలం చెల్లినది - నెమ్మది ఇంటర్నెట్‌కు మంచిది (G729) మద్దతు: Yealink, Cisco`,
    },
    ringGroup: {
      ringing: `రింగ్ అవుతోంది`,
      created: `రింగ్ గ్రూప్ సృష్టించబడింది`,
      updated: `రింగ్ గ్రూప్ నవీకరించబడింది`,
      saveFailed: `రింగ్ గ్రూప్ సేవ్ చేయడం విఫలమైంది`,
      notType: `ఇది రింగ్ గ్రూప్ కాదు`,
      notTypeDescription: `ఈ ఎక్స్‌టెన్షన్ రింగ్ గ్రూప్ కాదు. ఇతర రకాలను సవరించడానికి ఎక్స్‌టెన్షన్ల జాబితాను ఉపయోగించండి.`,
      namePlaceholder: `రింగ్ గ్రూప్ పేరు`,
      ringStrategy: `రింగ్ వ్యూహం`,
      ringAll: `అందరికీ రింగ్`,
      pagingGroup: `పేజింగ్ గ్రూప్`,
      ringbackAudio: `రింగ్‌బ్యాక్ ఆడియో`,
      createButton: `రింగ్ గ్రూప్ సృష్టించు`,
      updateButton: `రింగ్ గ్రూప్ నవీకరించు`,
    },
    callQueue: {
      created: `కాల్ క్యూ సృష్టించబడింది`,
      updated: `కాల్ క్యూ నవీకరించబడింది`,
      saveFailed: `కాల్ క్యూ సేవ్ చేయడం విఫలమైంది`,
      notType: `ఇది కాల్ క్యూ కాదు`,
      queueSettings: `క్యూ సెట్టింగ్‌లు`,
      ringStrategy: `రింగ్ వ్యూహం`,
      strategyRingAll: `అందరికీ రింగ్`,
      strategyRoundRobin: `రౌండ్ రాబిన్`,
      strategyLongestIdle: `ఎక్కువసేపు ఖాళీగా ఉన్నవారు`,
      strategyRandom: `యాదృచ్ఛికం`,
      strategyLeastTalkTime: `అతి తక్కువ మాట్లాడిన సమయం`,
      strategyFewestCalls: `అతి తక్కువ కాల్‌లు`,
      preCallAnnouncement: `ప్రీ-కాల్ ప్రకటన`,
      maxNoAnswer: `సమాధానం లేని గరిష్ఠ సంఖ్య (ప్రయత్నాలు)`,
      busyDelayTime: `బిజీ ఆలస్య సమయం (సెకన్లు)`,
      rejectDelayTime: `తిరస్కరణ ఆలస్య సమయం (సెకన్లు)`,
      wrapUpTime: `ర్యాప్-అప్ సమయం (సెకన్లు)`,
      noAnswerDelayTime: `సమాధానం లేని ఆలస్య సమయం (సెకన్లు)`,
      maxRingDuration: `గరిష్ఠ రింగ్ వ్యవధి (సెకన్లు)`,
      maxCallersInQueue: `క్యూలో గరిష్ఠ కాలర్‌లు`,
      maxWaitTime: `గరిష్ఠ నిరీక్షణ సమయం (సెకన్లు)`,
      timeoutDestination: `టైమ్అవుట్ గమ్యం`,
      escapeDestination: `ఎస్కేప్ గమ్యం`,
      destinationSearchPlaceholder: `ఎక్స్‌టెన్షన్లు శోధించండి...`,
      destinationNoMatches: `ఆ శోధనకు సరిపోయే ఎక్స్‌టెన్షన్ లేదు.`,
      destinationCurrentUnlisted: `{{value}} (ప్రస్తుతం)`,
      periodicAnnouncements: `కాలానుగుణ ప్రకటనలు`,
      positionAnnouncementFrequency: `స్థాన ప్రకటన ఫ్రీక్వెన్సీ`,
      positionAnnouncementMessage: `స్థాన ప్రకటన సందేశం`,
      queuePosition: `క్యూ స్థానం`,
      agentsNoun: `ఏజెంట్లు`,
      newButton: `కొత్త కాల్ క్యూ`,
      updateButton: `కాల్ క్యూ నవీకరించు`,
    },
    conferenceRoom: {
      moderatorPinValidation: `మోడరేటర్ PIN ఖచ్చితంగా 4 అంకెలు ఉండాలి.`,
      conferencePinValidation: `కాన్ఫరెన్స్ PIN ఖచ్చితంగా 4 అంకెలు ఉండాలి.`,
      holdMusicRequired: `హోల్డ్ మ్యూజిక్ తప్పనిసరి.`,
      created: `కాన్ఫరెన్స్ రూమ్ సృష్టించబడింది`,
      updated: `కాన్ఫరెన్స్ రూమ్ నవీకరించబడింది`,
      saveFailed: `కాన్ఫరెన్స్ రూమ్ సేవ్ చేయడం విఫలమైంది`,
      notType: `ఇది కాన్ఫరెన్స్ రూమ్ కాదు`,
      moderatorRequired: `మోడరేటర్ తప్పనిసరి`,
      moderatorPin: `మోడరేటర్ PIN`,
      userPinRequired: `వినియోగదారు PIN తప్పనిసరి`,
      conferencePin: `కాన్ఫరెన్స్ PIN`,
      defaultMuted: `డిఫాల్ట్‌గా మ్యూట్`,
      announceNewParticipants: `కొత్త పాల్గొనేవారిని ప్రకటించు`,
      conferenceGreeting: `కాన్ఫరెన్స్ గ్రీటింగ్`,
      selectHoldMusic: `హోల్డ్ మ్యూజిక్ ఎంచుకోండి`,
      createButton: `కాన్ఫరెన్స్ రూమ్ సృష్టించు`,
      updateButton: `కాన్ఫరెన్స్ రూమ్ నవీకరించు`,
    },
    virtualFax: {
      invalidEmail: `దయచేసి చెల్లుబాటు అయ్యే ఇమెయిల్ చిరునామాను నమోదు చేయండి.`,
      passwordsDoNotMatch: `పాస్‌వర్డ్‌లు సరిపోలడం లేదు.`,
      passwordMinLength: `సెక్యూర్ ఫ్యాక్స్ పాస్‌వర్డ్ కనీసం 8 అక్షరాలు ఉండాలి.`,
      created: `వర్చువల్ ఫ్యాక్స్ సృష్టించబడింది`,
      updated: `వర్చువల్ ఫ్యాక్స్ నవీకరించబడింది`,
      saveFailed: `వర్చువల్ ఫ్యాక్స్ సేవ్ చేయడం విఫలమైంది`,
      secureFaxLinkResent: `సెక్యూర్ ఫ్యాక్స్ లింక్ మళ్ళీ పంపబడింది`,
      resendFailed: `మళ్ళీ పంపడం విఫలమైంది`,
      resendSaveFailed: `సెక్యూర్ ఫ్యాక్స్ లింక్ మళ్ళీ పంపడం విఫలమైంది`,
      notType: `ఇది వర్చువల్ ఫ్యాక్స్ కాదు`,
      receivingOptions: `స్వీకరణ ఎంపికలు`,
      receivingEmailDestination: `స్వీకరించే ఇమెయిల్ గమ్యం`,
      secureFax: `సెక్యూర్ ఫ్యాక్స్`,
      download: `డౌన్‌లోడ్`,
      singleDownload: `ఒకే డౌన్‌లోడ్`,
      download5: `5 డౌన్‌లోడ్‌లు`,
      download10: `10 డౌన్‌లోడ్‌లు`,
      download15: `15 డౌన్‌లోడ్‌లు`,
      failedLoginRetries: `తప్పు పాస్‌వర్డ్ ప్రయత్నాల పరిమితి`,
      retries5: `5 పునఃప్రయత్నాలు`,
      retries10: `10 పునఃప్రయత్నాలు`,
      retries15: `15 పునఃప్రయత్నాలు`,
      linkExpiresIn: `డౌన్‌లోడ్ లింక్ గడువు ముగిసే వ్యవధి`,
      day1: `1 రోజు`,
      day2: `2 రోజులు`,
      day3: `3 రోజులు`,
      day7: `7 రోజులు`,
      day15: `15 రోజులు`,
      day30: `30 రోజులు`,
      changePasswordOptional: `సెక్యూర్ ఫ్యాక్స్ పాస్‌వర్డ్ మార్చండి (ఐచ్ఛికం)`,
      userGroupsDescription: `ఈ వర్చువల్ ఫ్యాక్స్‌కు ఏ వినియోగదారు గ్రూప్‌లకు యాక్సెస్ ఉండాలో ఎంచుకోండి.`,
      sending: `పంపుతోంది…`,
      resendSecureFaxLink: `సెక్యూర్ ఫ్యాక్స్ లింక్ మళ్ళీ పంపండి`,
      createButton: `వర్చువల్ ఫ్యాక్స్ మెషిన్ సృష్టించు`,
      updateButton: `వర్చువల్ ఫ్యాక్స్ మెషిన్ నవీకరించు`,
    },
    shared: {
      generalOptions: `సాధారణ ఎంపికలు`,
      voicemailTimeZone: `వాయిస్‌మెయిల్ టైమ్ జోన్`,
      voicemailPassword: `వాయిస్‌మెయిల్ పాస్‌వర్డ్`,
      playVoicemailInstructions: `వాయిస్‌మెయిల్ సూచనలను ప్లే చేయి`,
      notificationOptions: `నోటిఫికేషన్ ఎంపికలు`,
      sendEmailNotifications: `ఇమెయిల్ నోటిఫికేషన్‌లు పంపు`,
      messageEmailAddress: `సందేశ ఇమెయిల్ చిరునామా`,
      emailPlaceholder: `email@example.com`,
      attachVoicemailMessage: `వాయిస్‌మెయిల్ సందేశాన్ని జత చేయి`,
      deleteMessageAfterSend: `పంపిన తర్వాత సందేశాన్ని తొలగించు`,
      outgoingMessage: `అవుట్‌గోయింగ్ సందేశం`,
      useSystemGreetings: `సిస్టమ్ గ్రీటింగ్‌లను ఉపయోగించు`,
      customGreeting: `కస్టమ్ గ్రీటింగ్`,
    },
    sharedVoicemail: {
      created: `షేర్డ్ వాయిస్‌మెయిల్ సృష్టించబడింది`,
      updated: `షేర్డ్ వాయిస్‌మెయిల్ నవీకరించబడింది`,
      saveFailed: `షేర్డ్ వాయిస్‌మెయిల్ సేవ్ చేయడం విఫలమైంది`,
      notType: `ఇది షేర్డ్ వాయిస్‌మెయిల్ కాదు`,
      userGroupsDescription: `ఈ షేర్డ్ వాయిస్‌మెయిల్ బాక్స్‌కు ఏ వినియోగదారు గ్రూప్‌లకు యాక్సెస్ ఉండాలో ఎంచుకోండి.`,
      createButton: `షేర్డ్ వాయిస్‌మెయిల్ సృష్టించు`,
      updateButton: `షేర్డ్ వాయిస్‌మెయిల్ నవీకరించు`,
    },
  },

  updateLocation: {
    title: `స్థానం నవీకరించు`,
    description: `{{count}} లైన్ కోసం స్థానం నవీకరించాలా?`,
    confirm: `స్థానం నవీకరించు`,
  },

  pickList: {
    assignedWithLimit: `కేటాయించిన {{itemNoun}}: {{count}} / {{max}}`,
    assigned: `కేటాయించిన {{itemNoun}}: {{count}}`,
    noneAssigned: `ఇంకా {{itemNoun}} జోడించబడలేదు.`,
    remove: `తీసివేయి`,
    available: `అందుబాటులో ఉన్న {{itemNoun}}: {{count}}`,
    searchAvailable: `అందుబాటులో ఉన్న {{itemNoun}} శోధించండి`,
    maxReached: `గరిష్ఠంగా {{max}} {{itemNoun}} చేరుకుంది.`,
    noneAvailable: `{{itemNoun}} అందుబాటులో లేవు.`,
  },

  localPresence: {
    0: `ఆఫ్`,
    1: `గ్రూప్ 1`,
    2: `గ్రూప్ 2`,
    3: `గ్రూప్ 3`,
    4: `గ్రూప్ 4`,
    5: `గ్రూప్ 5`,
    6: `గ్రూప్ 6`,
  },

  codecOptions: {
    opus: `నెమ్మది ఇంటర్నెట్‌కు ఉత్తమం (OPUS) — మద్దతు: వెబ్ ఫోన్‌లు, Yealink`,
    pcmu: `ప్రామాణికం (PCMU) — మద్దతు: అన్ని ఫోన్ రకాలు`,
    g722: `HD ఆడియో (G722) — మద్దతు: Yealink, Cisco`,
    g729: `కాలం చెల్లినది — నెమ్మది ఇంటర్నెట్‌కు మంచిది (G729) — మద్దతు: Yealink, Cisco`,
    default: `సిస్టమ్ డిఫాల్ట్`,
  },

  timezones: {
    america_new_york: `Eastern Time`,
    america_chicago: `Central Time`,
    america_denver: `Mountain Time`,
    america_los_angeles: `Pacific Time`,
    america_phoenix: `Arizona`,
    america_anchorage: `Alaska Time`,
    pacific_honolulu: `Hawaii Time`,
    america_toronto: `Toronto (Eastern)`,
    america_vancouver: `పసిఫిక్ సమయం - బ్రిటిష్ కొలంబియా (PCT)`,
    europe_london: `London (GMT/BST)`,
    europe_paris: `Paris (CET)`,
    australia_sydney: `Sydney (AEST)`,
    utc: `UTC`,
  },

  // ---------------------------------------------------------------------------
  // Create extension options
  // ---------------------------------------------------------------------------
  createOptions: {
    add: `జోడించు`,
    addLine: `లైన్ జోడించు`,
    addRingGroup: `రింగ్ గ్రూప్ జోడించు`,
    addCallQueue: `కాల్ క్యూ జోడించు`,
    addConferenceRoom: `కాన్ఫరెన్స్ రూమ్ జోడించు`,
    addVirtualFax: `వర్చువల్ ఫ్యాక్స్ మెషిన్ జోడించు`,
    addSharedVoicemail: `షేర్డ్ వాయిస్‌మెయిల్ బాక్స్ జోడించు`,
  },

  // ---------------------------------------------------------------------------
  // Ordering options
  // ---------------------------------------------------------------------------
  ordering: {
    lastUpdatedNewest: `చివరి నవీకరణ (కొత్తది ముందు)`,
    lastUpdatedOldest: `చివరి నవీకరణ (పాతది ముందు)`,
    createdNewest: `సృష్టించబడింది (కొత్తది ముందు)`,
    createdOldest: `సృష్టించబడింది (పాతది ముందు)`,
    nameAZ: `పేరు (A\u2013Z)`,
    nameZA: `పేరు (Z\u2013A)`,
    numberAsc: `నంబర్ (ఆరోహణ)`,
    numberDesc: `నంబర్ (అవరోహణ)`,
  },

  // ---------------------------------------------------------------------------
  // Slug-to-label mappings (for breadcrumbs)
  // ---------------------------------------------------------------------------
  slugLabels: {
    line: `లైన్`,
    ringGroup: `రింగ్ గ్రూప్`,
    callQueue: `కాల్ క్యూ`,
    conferenceRoom: `కాన్ఫరెన్స్ రూమ్`,
    virtualFax: `వర్చువల్ ఫ్యాక్స్ మెషిన్`,
    sharedVoicemail: `షేర్డ్ వాయిస్‌మెయిల్ బాక్స్`,
  },

  // ---------------------------------------------------------------------------
  // List labels for type slugs
  // ---------------------------------------------------------------------------
  listLabels: {
    lines: `లైన్లు`,
    ringGroups: `రింగ్ గ్రూప్‌లు`,
    callQueues: `కాల్ క్యూలు`,
    conferenceRooms: `కాన్ఫరెన్స్ రూమ్‌లు`,
    virtualFax: `వర్చువల్ ఫ్యాక్స్`,
    sharedVoicemail: `షేర్డ్ వాయిస్‌మెయిల్`,
  },
} satisfies ExtensionsLocale
